import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { NAVIGATION, NAVIGATION_LINKS } from '../constants';
import MegaMenu from './MegaMenu';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleNavClick = (e: React.MouseEvent, link: typeof NAVIGATION_LINKS[0]) => {
    e.preventDefault();
    setIsOpen(false);

    if (link.type === 'page') {
      navigate(link.href);
      window.scrollTo(0, 0);
    } else {
      const hash = link.href.split('#')[1];
      
      if (location.pathname === '/') {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
  };

  return (
    <header className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
      scrolled 
        ? 'bg-brand-black/95 backdrop-blur-md border-b border-brand-gray'
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center" onClick={() => window.scrollTo(0,0)}>
          <img src="/LOGO.svg" alt="DisruptNext" className="h-9 w-auto" />
        </Link>

        {/* Desktop: Mega Menu */}
        <MegaMenu />

        {/* Desktop: CTA Button */}
        <Link
          to="/contact"
          className="hidden lg:flex items-center gap-2 bg-brand-red hover:bg-red-700 text-white px-5 py-2.5 text-xs font-bold tracking-[0.15em] rounded transition-all duration-200 hover:shadow-lg hover:shadow-red-500/20"
        >
          LET'S TALK
          <ArrowRight className="w-3 h-3" />
        </Link>

        {/* Mobile Menu Toggle */}
        <button 
          className="lg:hidden text-brand-white p-2"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 top-[60px] bg-brand-black/98 backdrop-blur-lg z-50 overflow-y-auto"
          >
            <div className="p-8 pt-8 pb-24">
              {/* Mobile nav items with expandable sections */}
              {NAVIGATION.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="mb-2"
                >
                  {item.type === 'mega' ? (
                    <MobileExpandableSection item={item} onClose={() => setIsOpen(false)} />
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className="block text-2xl font-black text-brand-white py-3 hover:text-brand-red hover:pl-4 transition-all"
                    >
                      {item.name}
                    </Link>
                  )}
                </motion.div>
              ))}

              {/* Mobile CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-8 pt-8 border-t border-white/10"
              >
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center gap-2 w-full bg-brand-red text-white py-4 rounded text-sm font-bold tracking-[0.15em]"
                >
                  LET'S TALK
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// ============================================================
// MOBILE EXPANDABLE SECTION
// ============================================================

const MobileExpandableSection: React.FC<{ item: typeof NAVIGATION[0]; onClose: () => void }> = ({ item, onClose }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-2xl font-black text-brand-white py-3 hover:text-brand-red transition-all"
      >
        {item.name}
        <motion.span
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-brand-red">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.span>
      </button>
      
      <AnimatePresence>
        {expanded && item.megaMenu && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pl-4 pb-4 space-y-6">
              {item.megaMenu.map((column, colIdx) => (
                <div key={colIdx}>
                  <h4 className="text-[10px] font-bold tracking-[0.25em] text-brand-red mb-3 uppercase">
                    {column.title}
                  </h4>
                  <ul className="space-y-2">
                    {column.items.map((menuItem, itemIdx) => (
                      <li key={itemIdx}>
                        <Link
                          to={menuItem.href}
                          onClick={onClose}
                          className="block text-sm text-white/70 hover:text-white transition-colors py-1"
                        >
                          {menuItem.name}
                          {menuItem.description && (
                            <span className="block text-xs text-white/30 mt-0.5">{menuItem.description}</span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Header;
